import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { Dialog } from '@mui/material';

import { getObjFromId } from '../../../utils/directusFunctions';
import { uuid } from '../../../utils/stringUtils';
import Button from '../../formElements/Button';
import DateSelect from '../../formElements/DateSelect';
import SingleSelect from '../../formElements/SingleSelect';
import TextAreaField from '../../formElements/TextAreaField';
import TextInputField from '../../formElements/TextInputField';

import {
    MAX_COMPANY_NAME,
    MAX_DESCRIPTION_LENGTH,
    MAX_JOB_TITLE,
    validateEdit
} from './validateWorkHistory';

import form from '../../../commonStyles/formStyles.module.css';
import modal from '../../../commonStyles/modal.module.css';

const EditWorkHistory = ({
    employment,
    participant,
    isDisabled,
    editType,
    isOpen,
    onCancel,
    onUpdate,
    onAdd
}) => {
    // LOCAL STATE
    const initialState = {
        id: uuid(),
        companyName: '',
        description: '',
        employmentTypeId: '',
        endDate: '',
        jobTitle: '',
        reasonForLeavingId: '',
        startDate: ''
    };

    const [newEntry, setNewEntry] = useState(initialState);
    const [initialStateOnEntry, setInitialStateOnEntry] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);

    // STORE STATE
    const employmentTypeDetails = useSelector(
        (state) => state.entities.directusService.employmentTypeDetails
    );
    const reasonForLeavingDetails = useSelector(
        (state) => state.entities.directusService.reasonForLeavingDetails
    );

    // USEEFFECTS
    useEffect(() => {
        if (editType === 'edit') {
            const updatedEmployment = {
                ...employment,
                description: employment.description === null ? '' : employment.description
            };
            setNewEntry(updatedEmployment);
            setInitialStateOnEntry(updatedEmployment);
        } else setNewEntry((prev) => ({ ...prev, participantId: participant?.id }));
    }, [editType, participant]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onUpdateValue = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onUpdateStartDate = (startDate) => {
        clearError('endDate');
        setNewEntry((prev) => ({ ...prev, startDate }));
    };

    const onClose = () => {
        setNewEntry(initialState);
        setErrors({});
        onCancel();
    };

    const onSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const newErrors = validateEdit(newEntry, initialStateOnEntry, editType);
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }
        editType === 'create' ? onAdd(newEntry) : onUpdate(newEntry);
        setNewEntry(initialState);
        setErrors({});
    };

    return (
        <Dialog
            className="muiDialogWrapper"
            open={isOpen}
            onClose={onClose}
            fullWidth
            aria-labelledby="form-dialog-title">
            <div className={modal.modalHeader} id="work-history-modal-title">
                <h3>{editType === 'create' ? 'Add' : 'Edit'} Past Employment</h3>
            </div>
            <div className={modal.modalContent}>
                <form
                    onSubmit={onSubmit}
                    className={form.formWrapper}
                    data-testid="edit_work_history_form">
                    <div className={form.formSection}>
                        <div className={form.formColumn}>
                            <TextInputField
                                id="companyName"
                                label="Company Name"
                                placeholder="Enter company name..."
                                disabled={isDisabled}
                                mandatory={true}
                                maxLength={MAX_COMPANY_NAME}
                                value={newEntry.companyName || ''}
                                error={errors.companyName}
                                onChange={(e) => onUpdateValue('companyName', e.target.value)}
                            />
                            <SingleSelect
                                id="employmentTypeId"
                                label="Employment Type"
                                placeholder="Select employment type..."
                                disabled={isDisabled}
                                mandatory={true}
                                customClass="absolutePosition"
                                menuItems={employmentTypeDetails}
                                selectedId={newEntry?.employmentTypeId}
                                selected={getObjFromId(
                                    employmentTypeDetails,
                                    newEntry.employmentTypeId
                                )}
                                error={errors.employmentTypeId}
                                onChange={(chosenId) => onUpdateValue('employmentTypeId', chosenId)}
                            />
                            <TextInputField
                                id="jobTitle"
                                label="Job Title"
                                placeholder="Enter job title..."
                                disabled={isDisabled}
                                mandatory={true}
                                maxLength={MAX_JOB_TITLE}
                                value={newEntry.jobTitle || ''}
                                error={errors.jobTitle}
                                onChange={(e) => onUpdateValue('jobTitle', e.target.value)}
                            />
                        </div>
                        <div className={form.formColumn}>
                            <DateSelect
                                id="startDate"
                                label="Start Date"
                                disabled={isDisabled}
                                value={newEntry.startDate || ''}
                                error={errors.startDate}
                                onDateChange={(date) => onUpdateStartDate(date)}
                            />
                            <DateSelect
                                id="endDate"
                                label="End Date"
                                disabled={isDisabled}
                                value={newEntry.endDate || ''}
                                error={errors.endDate}
                                onDateChange={(date) => onUpdateValue('endDate', date)}
                            />
                            <SingleSelect
                                id="reasonForLeavingId"
                                label="Reason For Leaving"
                                placeholder="Select reason for leaving..."
                                disabled={isDisabled}
                                customClass="absoluteAndHeightControl"
                                menuItems={reasonForLeavingDetails}
                                selectedId={newEntry.reasonForLeavingId}
                                selected={getObjFromId(
                                    reasonForLeavingDetails,
                                    newEntry.reasonForLeavingId
                                )}
                                onChange={(chosenId) =>
                                    onUpdateValue('reasonForLeavingId', chosenId)
                                }
                            />
                        </div>
                    </div>
                    <div className={form.formSection}>
                        <div className={form.formColumn}>
                            <TextAreaField
                                id="description"
                                label="Description"
                                placeholder={'Enter job description...'}
                                disabled={isDisabled}
                                maxLength={MAX_DESCRIPTION_LENGTH}
                                value={newEntry.description}
                                count={`${newEntry?.description?.length || 0}/${MAX_DESCRIPTION_LENGTH}`}
                                error={errors.description}
                                onChange={(e) => onUpdateValue('description', e.target.value)}
                            />
                        </div>
                    </div>

                    <div className={modal.actionButtons}>
                        <Button
                            id="editWorkHistoryButton"
                            disabled={isDisabled || isUpdateButtonDisabled}
                            content={editType === 'edit' ? 'Update' : 'Add'}
                            customErrorClass="formActionsError"
                            error={errors.button}
                            clearError={() => clearError('button')}
                        />
                        <Button
                            id="editEducationCancelButton"
                            type="button"
                            content="Cancel"
                            onClick={onClose}
                        />
                    </div>
                </form>
            </div>
        </Dialog>
    );
};

EditWorkHistory.propTypes = {
    employment: PropTypes.object,
    participant: PropTypes.object,
    isDisabled: PropTypes.bool,
    editType: PropTypes.string,
    isOpen: PropTypes.bool,
    onCancel: PropTypes.func,
    onUpdate: PropTypes.func,
    onAdd: PropTypes.func
};

export default EditWorkHistory;
